import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { 
  format, 
  addDays, 
  differenceInMinutes, 
  compareAsc 
} from "date-fns";
import toast from 'react-hot-toast';
import { 
  useMediaQuery, 
  useTheme, 
  Box, 
  Typography, 
  CircularProgress, 
  FormControl, 
  MenuItem, 
  InputLabel, 
  Select, 
  alpha
} from '@mui/material';
import {
  FlightTakeoff,
  FlightLand,
  AccessTime,
  MoreTime
} from "@mui/icons-material";
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT, EXCLUDED_FLIGHT_STATUS } from 'state/constants';
import api from 'state/axios';
import { 
  removeItemByValueImmutable, 
  filterFlights,
  sortMultipleFields,
} from 'state/helpers';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import ScrollToTop from 'components/ScrollToTop';
import MetricCard from 'components/MetricCard';
import FlightFilterDialog from 'components/FlightFilterDialog';
import SortBarChart from 'components/chartNivo/SortBarChart';


const TATDashboard = () => {
  useAuthCheck();
  const timeMode = useSelector((state) => state.timemode);
  const flightsData = useSelector((state) => state.flights);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.up('sm') && theme.breakpoints.up('lg'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
    
  const { startDate, endDate } = useDateRangeContext();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);  
  const [filters, setFilters] = useState(null);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false); 
  const [port, setPort] = useState('ALL');

  const [tatTemplates, setTatTemplates] = useState([]); 
  const [tatInstances, setTatInstances] = useState([]);
  const [processedFlights, setProcessedFlights] = useState([]);
    

  const fetchTATTemplates = useCallback(async () => {
    const res = await api.get('/tattemplates/active');
    return res.data;
  }, []);

  const fetchTATInstances = useCallback(async () => {
    if (!startDate || !endDate) return []; 

    const payload = { startDate: addDays(startDate, -1), endDate: addDays(endDate, 1)}
    const res = await api.post('/tatinstances', payload);

    if (res.data) {        
      const filteredInstances = res.data?.filter(instance => {
        const flightDateToCompare = timeMode === 'UTC' 
          ? instance.flightDate 
          : instance.flightDateLocalBase;
      
        return (
          compareAsc(flightDateToCompare, startDate) >= 0 &&
          compareAsc(flightDateToCompare, endDate) <= 0
        );
      });
      
      return filteredInstances;
    }

    return [];
  }, [timeMode, startDate, endDate]);
  
  const fetchData = useCallback(async () => {
    try {      
      setError(null);     
      setLoading(true);
      
      const [
        tatTemplatesData,
        tatInstancesData,
      ] = await Promise.all([
        fetchTATTemplates(),
        fetchTATInstances(),
      ]);
      setTatTemplates(tatTemplatesData || []);        
      setTatInstances(tatInstancesData || []);        
      
    } catch (error) {
      setError(error);
      toast.error(error?.message || "Failed to fetch data!");
    } finally {
      setLoading(false);
    }
  }, [fetchTATTemplates, fetchTATInstances]);
    
  useEffect(() => {
    fetchData();        
  }, [fetchData]);

  useEffect(() => {
    const processFlights = async () => {
      try {
        setError(null);
        setLoading(true);
       
        const imutated = removeItemByValueImmutable(
          flightsData,
          "flightStatus",
          EXCLUDED_FLIGHT_STATUS
        );
        const filtered = filterFlights({ flights: imutated, filters, timeMode });
        const sorted = sortMultipleFields(filtered, [
          { field: "fullEtd", direction: "asc" },
          { field: "flightReg", direction: "asc" },
        ]);

        const enriched = sorted.map((f) => {
          const matchedInstance = tatInstances?.find((i) => {
            const sameDate = new Date(i?.flightDate).getTime() === new Date(f?.flightDate).getTime();
            return (
              sameDate &&
              i.flightDesc === f.flightDesc &&
              i.dep === f.dep
            );
          });

          if (matchedInstance) {
            const total = matchedInstance.tasks.length;
            const done = matchedInstance.tasks.filter(t => t.status === "completed").length;
            const progress = total ? Math.round((done / total) * 100) : 0;
            
            return {
              ...f,
              tasks: matchedInstance.tasks || [],
              templateId: matchedInstance.templateId || null,
              instanceId: matchedInstance._id,
              isCompleted: matchedInstance.isCompleted,
              progress,
            };
          } else {
            // Hàm chấm điểm template dựa trên mức độ match
            const scoreTemplate = (t) => {
              let score = 0;

              if (!t.acTypeCodes?.length || t.acTypeCodes.includes(f.acType)) score++;
              if (!t.acRegs?.length || t.acRegs.includes(f.acReg)) score++;
              if (!t.airportCodes?.length || t.airportCodes.includes(f.dep)) score++;
              if (!t.sectors?.length || t.sectors.includes(f.sector)) score++;

              return score;
            };

            // Chọn template có điểm cao nhất
            const bestTemplate = tatTemplates?.reduce((best, t) => {
              const score = scoreTemplate(t);
              const bestScore = best ? scoreTemplate(best) : -1;
              return score > bestScore ? t : best;
            }, null);

            // Gán tasks từ template đã chọn
            const tasks = bestTemplate?.tasks?.map((t, i) => (
              {
                taskCode: t.taskCode,
                taskName: t.taskName,
                preceding: t.preceding,
                startTime: t.startTime || 0,
                endTime: t.endTime || 0,
                duration: t.duration || 0,
                priority: t.priority || 0,
                actualStartDate: null,
                actualEndDate: null,
                actualDuration: null,
                remarks: "",
                status: "pending",
                assignedRole: "Unassigned",
              }
            )) || [];
            
            return { 
              ...f, 
              tasks, 
              templateId: bestTemplate?._id || null,
              progress: 0,
            };
          }
        });

        setProcessedFlights(enriched);
      } catch (error) {
        setError(error);
        toast.error(error?.message || "Failed to load flights!");
      } finally {
        setLoading(false);
      }
    };

    processFlights();
  }, [timeMode, flightsData, filters, tatInstances, tatTemplates]);

  const sortedFlights = useMemo(() => {
    const sorted = [...processedFlights];
    sorted.sort((a, b) => {
      const aEtd = a.fullEtd ? new Date(a.fullEtd) : null;
      const bEtd = b.fullEtd ? new Date(b.fullEtd) : null;
      const aReg = a.flightReg || '';
      const bReg = b.flightReg || '';
            
      if (aReg !== bReg) return aReg.localeCompare(bReg);
      if (!aEtd && !bEtd) return 0;
      if (!aEtd) return 1;
      if (!bEtd) return -1;
      return aEtd - bEtd;
    });

    return sorted;
  }, [processedFlights]);

  const enrichedFlights = useMemo(() => {
    if (port === 'ALL') return sortedFlights;
    return sortedFlights?.filter(flight => flight.dep === port);
  }, [sortedFlights, port]);
        
  const enrichedInstances = useMemo(() => {
    if (port === 'ALL') return tatInstances;
    return tatInstances?.filter(instance => instance.dep === port);
  }, [tatInstances, port]);
  
  const acTypeOptions = Array.isArray(processedFlights) ? [...new Set(processedFlights?.map(flight => flight.acType))].sort() : [];
  const flightRegOptions = Array.isArray(processedFlights) ? [...new Set(processedFlights?.map(flight => flight.flightReg))].sort() : [];
  const sectorTypeOptions = Array.isArray(processedFlights) ? [...new Set(processedFlights?.map(flight => flight.sectorType))].sort() : [];
  const departureTypeOptions = Array.isArray(processedFlights) ? [...new Set(processedFlights?.map(flight => flight.departureType))].sort() : [];
  const arrivalTypeOptions = Array.isArray(processedFlights) ? [...new Set(processedFlights?.map(flight => flight.arrivalType))].sort() : [];
  const airportOptions = useMemo(() => {
    if (!Array.isArray(processedFlights)) return [];
  
    const depPorts = processedFlights.map(flight => flight.dep);
    const arrPorts = processedFlights.map(flight => flight.arr);
    const combinedPorts = [...depPorts, ...arrPorts];
    
    return Array.from(new Set(combinedPorts)).sort(); 
  }, [processedFlights]);
  
  const handleFilterClose = () => setIsFilterDialogOpen(false);  
  const handleFilterApply = (filterValues) => {
    setFilters(filterValues); 
  };

  const analyzedInstances = useMemo(() => {
    const now = new Date();

    return enrichedInstances.map((instance) => {
      const tasks = instance.tasks || [];

      const starts = tasks.map((t) => t.actualStartDate && new Date(t.actualStartDate)).filter(Boolean);
      const ends = tasks.map((t) => t.actualEndDate && new Date(t.actualEndDate)).filter(Boolean);

      const actualStart = starts.length ? new Date(Math.min(...starts.map((d) => d.getTime()))) : null;
      const actualEnd = ends.length ? new Date(Math.max(...ends.map((d) => d.getTime()))) : null;
      const actualDuration = actualStart && actualEnd ? differenceInMinutes(actualEnd, actualStart) : null;

      const delayedTasks = tasks.filter((t) => {
        if (!t.actualStartDate) return false;
        const start = new Date(t.actualStartDate);
        const end = t.actualEndDate ? new Date(t.actualEndDate) : now;
        const duration = differenceInMinutes(end, start);
        return duration > (t.duration ?? 0);
      });

      return {
        ...instance,
        computed: {
          actualStart,
          actualEnd,
          actualDuration,
          delayedTasks,
          taskDelayCount: delayedTasks.length,
        },
      };
    });
  }, [enrichedInstances]);

  const indicators = useMemo(() => {
    const valid = analyzedInstances.filter((i) => i.computed.actualDuration !== null);
    const processedFlights = valid.length;
    const avgTAT = processedFlights > 0 
      ? valid.reduce((sum, i) => sum + i.computed.actualDuration, 0) / processedFlights 
      : 0;
    const taskDelayCount = valid.reduce((sum, i) => sum + i.computed.taskDelayCount, 0);
    const onTimeCount = valid.filter((i) => (i.computed.actualDuration ?? 0) <= 60).length;
    const onTimeRate = processedFlights ? (onTimeCount / processedFlights) * 100 : 0;

    return {
      totalFlights: enrichedFlights?.length,
      processedFlights,
      avgTAT: avgTAT.toFixed(1),
      taskDelayCount,
      onTimeRate: onTimeRate.toFixed(1),
    };
  }, [analyzedInstances, enrichedFlights]);

  const tatByPort = useMemo(() => {
    const obj = {};

    analyzedInstances.forEach((i) => {
      const key = i.dep || "N/A";
      const actualDuration = i.computed.actualDuration;

      if (!obj[key]) obj[key] = { key, count: 0, duration: 0 };

      if (actualDuration) {
        obj[key].count += 1;
        obj[key].duration += actualDuration;
      }
    });

    const arr = Object.values(obj).map(({ key, count }) => ({
      x: key,
      y: count,                       
    }));

    return arr;
  }, [analyzedInstances]);
  
  const delayTrend = useMemo(() => {
    const obj = {};

    analyzedInstances.forEach((i) => {
      const dateKey = timeMode === 'UTC' 
        ? format(i.flightDate, "yyyy-MM-dd") 
        : format(i.flightDateLocalBase, "yyyy-MM-dd");

      if (!obj[dateKey]) obj[dateKey] = { date: dateKey, taskDelayCount: 0 };

      obj[dateKey].taskDelayCount += i.computed.taskDelayCount;
    });

    return Object.values(obj)
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((m) => ({ x: m.date, y: m.taskDelayCount }));
  }, [timeMode, analyzedInstances]);

  const styles = useMemo(() => ({
    borderRadius: 1,
    border: "1px solid",
    borderColor: "neutral.deep",
    backgroundColor: "background.default",
  }), []);


  return (
    <Box className="main-container-box">                 
      <FlexBetween>
        <Header 
          title="TAT Dashboard" 
          subtitle={`${loading ? 'Loading...' : 'List of Figures'}`}
        />
        {!loading && (                    
          <Box display="flex" alignItems="center" gap={1}>
            <FormControl>
              <InputLabel>Port</InputLabel>
              <Select
                label="Port"
                value={port ? port : ''}
                onChange={(e) => setPort(e.target.value)}
                sx={{ 
                  height: '36px', 
                  width: isMobile ? '5rem' : '6rem',
                }}
              >
                <MenuItem key='ALL' value='ALL'>ALL</MenuItem>
                {airportOptions?.map((airport, index) => (
                  <MenuItem key={index} value={airport}>
                    {airport}
                  </MenuItem>
                ))}                                                               
              </Select>
            </FormControl>
          </Box>
        )}                
      </FlexBetween>          
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box 
          height={LAYOUT_HEIGHT} 
          display="flex" 
          flexDirection="column" 
          gap={2} 
          sx={{ overflowY: 'auto' }}
        >
          <Box
            display="grid"
            gridTemplateColumns="repeat(12, minmax(0, 1fr))"
            gridAutoRows={100}
            gap={2}           
            sx={{ "& > div": { gridColumn: (isDesktop || isTablet) ? "span 3" : "span 6" } }}
          >
            <MetricCard
              title="Flights"
              subtitle="Processed"
              value={indicators.processedFlights}
              icon={<FlightTakeoff/>}
              bgColor={alpha(theme.palette.info.dark, 0.6)}
            />
            <MetricCard
              title="TAT"
              subtitle="Mean TAT"
              value={`${indicators.avgTAT} min`}
              icon={<FlightLand/>}
              bgColor={alpha(theme.palette.primary.main, 0.6)}
            />
            <MetricCard
              title="On-Time Tasks"
              subtitle="Percentage"
              value={`${indicators.onTimeRate}%`}
              icon={<AccessTime/>}
              bgColor={alpha(theme.palette.success.dark, 0.6)}
            />
            <MetricCard
              title="Delayed Tasks"
              subtitle="Beyond duration"
              value={indicators.taskDelayCount}
              icon={<MoreTime/>}
              bgColor={alpha(theme.palette.warning.dark, 0.6)}
            />
          </Box>

          <Box
            display="grid"
            gridTemplateColumns="repeat(12, minmax(0, 1fr))"
            gridAutoRows={82}
            gap={2}
            sx={{ "& > div": { gridColumn: (isDesktop || isTablet) ? "span 6" : "span 12" } }}
          >
            <Box
              gridColumn="span 6"
              gridRow="span 4"
              sx={styles}
            >
              <SortBarChart data={tatByPort} title={"Processed flights by Port"} bottomLegend='Port' leftLegend='Flights' digit={0} />
            </Box>
            <Box
              gridColumn="span 6"
              gridRow="span 4"
              sx={styles}
            >
              <SortBarChart data={delayTrend} title={"Delayed tasks by Date"} bottomLegend='Date' leftLegend='Tasks' digit={0} />
            </Box>            
          </Box>
        </Box>       
      )}
                      
      <ScrollToTop />
      {isFilterDialogOpen &&
        <FlightFilterDialog
          open={isFilterDialogOpen}
          onClose={handleFilterClose}
          onApply={handleFilterApply}
          acTypeOptions={acTypeOptions} 
          flightRegOptions={flightRegOptions}
          sectorTypeOptions={sectorTypeOptions}
          departureTypeOptions={departureTypeOptions}
          arrivalTypeOptions={arrivalTypeOptions}
        />
      }
    </Box>
  );
};

export default TATDashboard;
